import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Phone, MessageCircle, Loader2, FileBarChart, BellRing } from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { Panel, SectionLabel, BarRow } from "@/components/admin/cc";

const RANGES = [7, 30, 90];

const ReportButtons = () => {
  const [busy, setBusy] = useState("");
  const run = async (key, url, okMsg) => {
    setBusy(key);
    try {
      const { data } = await api.post(url, {}, { headers: authHeaders() });
      data.sent ? toast.success(okMsg) : toast.error(data.reason || "Niet verstuurd.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt.");
    } finally { setBusy(""); }
  };
  const cls = "inline-flex items-center gap-2 rounded-xl border border-cc-border px-3.5 py-2 font-body text-xs font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors disabled:opacity-60";
  return (
    <div className="mt-5 pt-4 border-t border-cc-border flex flex-wrap items-center gap-2">
      <p className="font-body text-xs text-cc-faint mr-auto">Weekrapport elke maandag 8:00 · melding bij opvallend veel klikken</p>
      <button onClick={() => run("week", "/admin/reports/weekly/send-now", "Weekrapport verstuurd.")} disabled={!!busy} data-testid="report-weekly-send" className={cls}>
        {busy === "week" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileBarChart className="w-3.5 h-3.5" />} Weekrapport nu versturen
      </button>
      <button onClick={() => run("alert", "/admin/reports/click-alert/test", "Testmelding verstuurd.")} disabled={!!busy} data-testid="report-alert-test" className={cls}>
        {busy === "alert" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Testmelding
      </button>
    </div>
  );
};

const DayBars = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.phone + d.whatsapp));
  return (
    <div className="flex items-end gap-[3px] h-36" data-testid="clicks-per-day">
      {data.map((d) => {
        const total = d.phone + d.whatsapp;
        return (
          <div key={d.day} className="flex-1 h-full flex flex-col justify-end group relative" title={`${d.day}: ${d.phone} bellen, ${d.whatsapp} WhatsApp`}>
            <div className="w-full rounded-t-sm bg-[#25D366]" style={{ height: `${(d.whatsapp / max) * 100}%` }} />
            <div className={`w-full bg-cc-accent ${d.whatsapp ? "" : "rounded-t-sm"}`} style={{ height: `${(d.phone / max) * 100}%` }} />
            {total === 0 && <div className="w-full h-[2px] bg-cc-panel2" />}
          </div>
        );
      })}
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, tone, testid }) => (
  <div className="rounded-xl border border-cc-border bg-cc-panel2/50 px-4 py-3" data-testid={testid}>
    <p className="flex items-center gap-1.5 font-body text-[11px] uppercase tracking-wider text-cc-faint"><Icon className={`w-3.5 h-3.5 ${tone}`} />{label}</p>
    <p className="font-heading font-extrabold text-2xl text-cc-ink mt-1">{value}</p>
  </div>
);

export const CallClicksPanel = () => {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/clicks", { headers: authHeaders(), params: { days } });
      setD(data);
    } catch { setD(null); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const pages = d?.per_page || [];
  const maxPage = Math.max(1, ...pages.map((p) => p.phone + p.whatsapp));
  const fmt = (iso) => new Date(iso).toLocaleDateString("nl-BE", { day: "2-digit", month: "short" });

  return (
    <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="call-clicks">
      <Panel className="lg:col-span-2 p-6">
        <SectionLabel right={
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button key={r} onClick={() => setDays(r)} data-testid={`clicks-range-${r}`} className={`rounded-lg px-2.5 py-1 font-body text-xs font-semibold transition-colors ${days === r ? "bg-cc-accent text-white" : "text-cc-faint hover:text-cc-ink"}`}>{r}d</button>
            ))}
          </div>
        }>Klikken op bellen & WhatsApp</SectionLabel>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <Kpi icon={Phone} label="Bellen" value={d?.totals.phone ?? "—"} tone="text-cc-accent" testid="clicks-total-phone" />
          <Kpi icon={MessageCircle} label="WhatsApp" value={d?.totals.whatsapp ?? "—"} tone="text-[#25D366]" testid="clicks-total-whatsapp" />
          <Kpi icon={Phone} label="Vandaag" value={d ? d.today.phone + d.today.whatsapp : "—"} tone="text-cc-pos" testid="clicks-today" />
        </div>
        {d && <DayBars data={d.per_day} />}
        {d && (
          <div className="flex justify-between mt-2 font-body text-[11px] text-cc-faint">
            <span>{fmt(d.per_day[0].day)}</span>
            <span className="flex gap-3"><span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm bg-cc-accent" />Bellen</span><span className="flex items-center gap-1"><i className="w-2 h-2 rounded-sm bg-[#25D366]" />WhatsApp</span></span>
            <span>{fmt(d.per_day[d.per_day.length - 1].day)}</span>
          </div>
        )}
        <ReportButtons />
      </Panel>
      <Panel className="p-6">
        <SectionLabel>Klikken per pagina</SectionLabel>
        {pages.length === 0 ? <p className="font-body text-sm text-cc-faint">Nog geen klikken in deze periode.</p> : (
          <div data-testid="clicks-per-page">
            {pages.map((p) => <BarRow key={p.page} label={p.page} value={p.phone + p.whatsapp} max={maxPage} />)}
          </div>
        )}
      </Panel>
    </div>
  );
};
